
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Building, LineChart } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

const loginSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }),
  password: z.string().min(6, { message: 'A senha deve ter pelo menos 6 caracteres' }),
});

type LoginFormData = z.infer<typeof loginSchema>;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, user } = useAuth();
  const { toast: toastHook } = useToast();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/home';
  const isMobile = useIsMobile();

  useEffect(() => {
    // Se o usuário já estiver autenticado, redireciona para a página home
    if (user) {
      navigate(redirect);
    }
  }, [user, navigate, redirect]);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setLoading(true);
      const { email, password } = data;
      const { error } = await signIn(email, password);
      
      if (error) {
        console.error('Login error:', error);
        throw new Error(error.message);
      }
      
      if (isMobile) {
        // Usamos a toast do Sonner para mostrar mensagem em dispositivos móveis
        toast.success('Login bem-sucedido', {
          description: 'Bem-vindo ao Broker Base!',
          duration: 3000
        });
      } else {
        // Em desktops continuamos usando o toast do hook
        toastHook({
          title: 'Login bem-sucedido',
          description: 'Bem-vindo ao Broker Base!',
          variant: 'default',
        });
      }
      
      // Redirecionar para a página solicitada ou home
      navigate(redirect);
    } catch (error: any) {
      console.error('Login error:', error);
      
      if (isMobile) {
        toast.error('Erro ao fazer login', {
          description: error.message || 'Verifique suas credenciais e tente novamente.',
          duration: 5000
        });
      } else {
        toastHook({
          title: 'Erro ao fazer login',
          description: error.message || 'Verifique suas credenciais e tente novamente.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-soft">
      <div className="hidden lg:flex flex-1 bg-blue-dark items-center justify-center">
        <div className="max-w-lg text-center px-8">
          <h1 className="text-4xl text-white font-bold mb-6">Bem-vindo ao Broker Base</h1>
          <p className="text-lg text-white/80 mb-8">
            Seu painel de controle do mercado imobiliário.
          </p>
          <div className="bg-white/10 p-8 rounded-lg backdrop-blur-sm">
            <blockquote className="text-white/90 italic text-lg">
              "A plataforma Broker Base revolucionou a forma como trabalhamos com imóveis, trazendo dados precisos e análises profundas que nos ajudam a tomar decisões melhores."
            </blockquote>
            <div className="mt-4 text-white/80">
              <p className="font-medium">Lucas Pereira</p>
              <p className="text-sm">Corretor de Imoveis, Imóveis Premium</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center mb-8">
            <LineChart className="h-10 w-10 text-blue-dark" />
            <span className="text-2xl font-bold ml-2 text-blue-dark">Broker Base</span>
          </div>
          
          <div className="bg-white p-8 rounded-lg shadow-sm">
            <h2 className="text-2xl font-bold mb-6 text-center">Entrar na plataforma</h2>
            
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input 
                          placeholder="nome@exemplo.com" 
                          type="email" 
                          autoComplete="email"
                          disabled={loading}
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Senha</FormLabel>
                        <Link
                          to="/reset-password"
                          className="text-xs text-blue-dark hover:underline"
                        >
                          Esqueceu a senha?
                        </Link>
                      </div>
                      <FormControl>
                        <Input 
                          placeholder="Digite sua senha" 
                          type="password" 
                          autoComplete="current-password"
                          disabled={loading}
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <Button type="submit" className="w-full mt-6" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Entrando...
                    </>
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>
            </Form>
            
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Não tem uma conta?{' '}
                <Link to="/signup" className="text-blue-dark hover:underline">
                  Criar conta
                </Link>
              </p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              Ao acessar, você concorda com os{' '}
              <Link to="/termos-de-uso" className="text-blue-dark hover:underline">
                Termos de Uso
              </Link>{' '}
              e{' '}
              <Link to="/politica-de-privacidade" className="text-blue-dark hover:underline">
                Política de Privacidade
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
